<script setup>
// Resource-metadata card (Figma node 75-3719). A whole-card link: title +
// circular arrow, one or more icon-labelled metadata rows (type, updated,
// format/size…), and a short description. Inline SVG icons keyed by name.
defineProps({
  block: { type: Object, required: true },
  // { title, to, meta?: [{ icon, label }], body? }
})

const isExternal = (to) => typeof to === 'string' && /^https?:\/\//.test(to)

// Small icon set (24×24, currentColor). Falls back to a generic document glyph.
const icons = {
  document: 'M6 2h8l4 4v16H6zM14 2v4h4',
  updated:  'M4 12a8 8 0 1 1 2.3 5.6M4 20v-4h4',
  file:     'M14 3v5h5M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z',
  calendar: 'M7 3v3m10-3v3M4 8h16M5 6h14a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1',
}
const iconPath = (name) => icons[name] || icons.document
</script>

<template>
  <component
    :is="isExternal(block.to) ? 'a' : 'RouterLink'"
    v-bind="isExternal(block.to)
      ? { href: block.to, target: '_blank', rel: 'noopener noreferrer' }
      : { to: block.to }"
    class="resource-meta-card"
  >
    <div class="header-row">
      <h3 class="title">{{ block.title }}</h3>
      <span class="arrow-btn" aria-hidden="true">
        <svg viewBox="0 0 20 20" fill="none">
          <path d="M4 10h11m-4-5 5 5-5 5" stroke="currentColor" stroke-width="2"
            stroke-linecap="round" stroke-linejoin="round" />
        </svg>
      </span>
    </div>

    <ul v-if="block.meta && block.meta.length" class="meta">
      <li v-for="(m, i) in block.meta" :key="i" class="chip">
        <svg class="icon" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path :d="iconPath(m.icon)" stroke="currentColor" stroke-width="1.8"
            stroke-linecap="round" stroke-linejoin="round" />
        </svg>
        <span>{{ m.label }}</span>
      </li>
    </ul>

    <p v-if="block.body" class="body">{{ block.body }}</p>
  </component>
</template>

<style lang="scss" scoped>
.resource-meta-card {
  display: flex;
  flex-direction: column;
  gap: $space-4;
  padding: $space-6;
  background: var(--color-surface-warm);
  border: 1px solid var(--color-border-decorative);
  @include rounded($radius-lg);
  color: var(--color-ink);
  text-decoration: none;
  @include transition(opacity, transform);

  &:hover            { opacity: .9; }
  &:hover .arrow-btn { transform: translateX(2px); }
  &:focus-visible    { @include focus-ring; outline: none; }
  &:active           { transform: translateY(1px); }

  .header-row {
    display: flex;
    align-items: center;
    gap: $space-4;
  }

  .title {
    flex: 1;
    min-width: 0;
    margin: 0;
    font-size: $font-size-h4;
    line-height: $lh-heading-sm;
    letter-spacing: $ls-heading;
    font-weight: 500;
  }

  .arrow-btn {
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    border-radius: $radius-pill;
    background: var(--color-ink);
    color: var(--color-card-cream);
    @include transition(transform);

    svg { width: 20px; height: 20px; display: block; }
  }

  .meta {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: $space-3 $space-5;

    .chip {
      display: inline-flex;
      align-items: center;
      gap: $space-2;
      font-size: $font-size-sm;
      color: var(--color-text);
    }

    .icon {
      flex-shrink: 0;
      width: 20px;
      height: 20px;
      color: var(--color-text-muted);
    }
  }

  .body {
    margin: 0;
    font-size: $font-size-sm;
    line-height: $lh-base;
    color: var(--color-text);
  }
}
</style>
